import { mount, unmount, type Snippet } from 'svelte';
import Tooltip from './Tooltip.svelte';

export type TooltipContent = { type: 'text'; text: string } | { type: 'snippet'; snippet: Snippet };

function createTooltipController(anchorEl: HTMLElement) {
	let tooltipInstance: any = null;
	let tooltipEl: HTMLElement | undefined;

	const tooltipState = $state({
		visible: false,
		x: 0,
		y: 0,
		text: undefined as string | undefined,
		snippet: undefined as Snippet | undefined,
		bindEl: (el: HTMLElement) => {
			tooltipEl = el;
		}
	});

	function positionTooltip() {
		if (!tooltipInstance) {
			return;
		}

		const rect = anchorEl.getBoundingClientRect();
		const gap = 8;
		const margin = 8;

		const tooltipWidth = tooltipEl?.offsetWidth ?? 0;
		const tooltipHeight = tooltipEl?.offsetHeight ?? 0;

		let top = rect.bottom + gap;
		let left = rect.left + rect.width / 2;

		if (top + tooltipHeight > window.innerHeight - margin) {
			top = rect.top - tooltipHeight - gap;
		}
		if (top < margin) {
			top = margin;
		}

		if (left - tooltipWidth / 2 < margin) {
			left = margin + tooltipWidth / 2;
		}
		if (left + tooltipWidth / 2 > window.innerWidth - margin) {
			left = window.innerWidth - margin - tooltipWidth / 2;
		}

		tooltipState.x = left;
		tooltipState.y = top;
	}

	function show(content: TooltipContent) {
		if (tooltipInstance) {
			return;
		}

		if (content.type === 'snippet') {
			tooltipState.snippet = content.snippet;
			tooltipState.text = undefined;
		} else {
			tooltipState.text = content.text;
			tooltipState.snippet = undefined;
		}

		tooltipInstance = mount(Tooltip, {
			target: document.body,
			props: tooltipState
		});

		tooltipState.visible = true;

		requestAnimationFrame(() => {
			positionTooltip();
		});

		window.addEventListener('scroll', positionTooltip, true);
		window.addEventListener('resize', positionTooltip);
	}

	function hide(skipStateUpdate = false) {
		if (!tooltipInstance) {
			return;
		}

		const instance = tooltipInstance;
		tooltipInstance = null;
		tooltipEl = undefined;

		window.removeEventListener('scroll', positionTooltip, true);
		window.removeEventListener('resize', positionTooltip);

		queueMicrotask(() => {
			if (!skipStateUpdate) {
				tooltipState.visible = false;
			}
			setTimeout(() => {
				unmount(instance);
			}, 150);
		});
	}

	return { show, hide };
}

export function tooltip(node: HTMLElement, param?: string | Snippet) {
	let showTimeout: number | undefined;
	const controller = createTooltipController(node);
	let currentParam: string | Snippet | undefined = param;

	function buildContent(): TooltipContent | undefined {
		const value = currentParam ?? node.getAttribute('aria-label') ?? undefined;
		if (!value) {
			return undefined;
		}
		if (typeof value === 'function') {
			return { type: 'snippet', snippet: value as Snippet };
		}
		return { type: 'text', text: value };
	}

	function onEnter() {
		const content = buildContent();
		if (!content) {
			return;
		}
		showTimeout = window.setTimeout(() => controller.show(content), 300);
	}

	function onLeave() {
		if (showTimeout) {
			clearTimeout(showTimeout);
			showTimeout = undefined;
		}
		controller.hide();
	}

	function onFocus() {
		const content = buildContent();
		if (content) {
			controller.show(content);
		}
	}

	node.addEventListener('mouseenter', onEnter);
	node.addEventListener('mouseleave', onLeave);
	node.addEventListener('focus', onFocus);
	node.addEventListener('blur', onLeave);

	return {
		update(newParam: string | Snippet | undefined) {
			currentParam = newParam;
		},
		destroy() {
			if (showTimeout) {
				clearTimeout(showTimeout);
				showTimeout = undefined;
			}
			controller.hide(true);
			node.removeEventListener('mouseenter', onEnter);
			node.removeEventListener('mouseleave', onLeave);
			node.removeEventListener('focus', onFocus);
			node.removeEventListener('blur', onLeave);
		}
	};
}
